import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, Container, Typography } from '@material-ui/core';
import { Col } from 'components/Col';
import { Row } from 'components/Row';
import { CustomCheckbox } from 'components/CustomCheckbox';
import useValidations from '../hooks/useValidations';
import CustomMultipleSelect from 'components/CustomMultipleSelect/CustomMultipleSelect';
import { CustomTextField } from 'components/CustomTextField';
import { DateMaskedInput } from 'components/MaskedInputs';
import { NewAutoCompleteField } from 'components/NewAutoCompleteField';
import { getFormattedCNPJ } from 'utils/DataFormatUtils';
import { diferenceMonths } from 'utils/DateUtils';
import { allProgramas } from 'models/escola';
import { changeContratoAssessorEditingValue } from 'store/modules/ContratosFluxoDescentralizado/Assessores/AssessoresActions';
import { RootState } from 'store';
import { Assessor, Empresa } from './__types';
import { CurrencyNewInput } from 'components/CustomCurrencyField/CurrencyNewInput';
import '../index.scss'
import { AlturaMenuEnum } from 'components/CustomMultipleSelect/__types';

export const ContratoAssessorDescentralizado = () => {
    const [dataInicioError, setDataInicioError] = useState('');
    const [dataFimError, setDataFimError] = useState('');
    const languageValues = useSelector((state: RootState) => state.languages.languageValues);
    const contratosAssessoresReducer = useSelector((state: RootState) => state.contratosDescentralizadosAssessores);
    const empresasReducer = useSelector((state: RootState) => state.empresas);
    const dispatch = useDispatch();
    const {
        valorAssessoriaIsValid,
        valorFormacaoIsValid,
        valorReuniaoIsValid,
        isContratoAtivoAssessor,
        idEmpresaIsValid,
        dataInicioIsValid,
        dataFimIsValid,
        validateDataFim,
        validateDataInicio,
        isEmpresaFieldNull,
        cpfAssessorIsValid,
        validaStatusContratoFluxo,
        valueProgramaEducacionaisIsValid,
    } = useValidations();

    const handleDataChange = useCallback((name, value) => {
        dispatch(changeContratoAssessorEditingValue(name, value));
    }, [dispatch])

    const handleCnpjChange = useCallback((value) => {
        if (value) {
            const currValue = value.value !== '' ? value : '';
            handleDataChange('idEmpresa', currValue.value);
            handleDataChange('cnpjEmpresa', currValue.cnpj);
        } else {
            handleDataChange('idEmpresa', '');
            handleDataChange('cnpjEmpresa', '');
            handleDataChange('cpfAssessores', []);
        }
    }, [handleDataChange]);

    const handleAssessorChange = (value: Assessor[]) => {
        const currValue = Array.isArray(value) && value.length > 0 ? value.map(it => it.value) : [];
        handleDataChange('cpfAssessores', currValue);
    };

    const onDataInicioChanged = (value: string) => {
        const error = validateDataInicio(
            value,
            contratosAssessoresReducer.contratoEditing.dataFim,
            {
                after: languageValues.dataInicioError,
                same: languageValues.dataInicioIgualDataFimError,
            }
        );
        setDataInicioError(error);

        if (!error) {
            setDataInicioError('');
        }

        let months: unknown = diferenceMonths(
            value,
            contratosAssessoresReducer.contratoEditing.dataFim
        );
        if (typeof months === 'string') {
            months = parseFloat(months);
        }
        if (isNaN(months as number)) {
            months = 0;
        }
        handleDataChange('totalMeses', Math.max(months as number, 0));
    };

    const onDataFimChanged = (value: string) => {
        const error = validateDataFim(
            value,
            contratosAssessoresReducer.contratoEditing.dataInicio,
            {
                before: languageValues.dataFimError,
                same: languageValues.dataFimIgualDataInicioError,
            }
        );
        setDataFimError(error);

        if (!error) {
            setDataFimError('');
        }
        const dataInicioErrorValue = validateDataInicio(
            contratosAssessoresReducer.contratoEditing.dataInicio,
            value,
            {
                after: languageValues.dataInicioError,
                same: languageValues.dataInicioIgualDataFimError,
            }
        );
        setDataInicioError(dataInicioErrorValue);

        let months: unknown = diferenceMonths(
            contratosAssessoresReducer.contratoEditing.dataInicio,
            value
        );
        if (typeof months === 'string') {
            months = parseFloat(months);
        }
        if (isNaN(months as number)) {
            months = 0;
        }
        handleDataChange('totalMeses', Math.max(months as number, 0));
    };

    const dataInicioValidacaoResultado = dataInicioIsValid(
        contratosAssessoresReducer.contratoEditing.dataInicio,
        contratosAssessoresReducer.contratoEditing.dataFim,
        {
            after: languageValues.dataInicioError,
            same: languageValues.dataInicioIgualDataFimError,
        }
    );

    const dataFimValidacaoResultado = dataFimIsValid(
        contratosAssessoresReducer.contratoEditing.dataFim,
        contratosAssessoresReducer.contratoEditing.dataInicio,
        {
            before: languageValues.dataFimError,
            same: languageValues.dataFimIgualDataInicioError,
        }
    );

    return (
        <Container className='componentsDescentralizado__container'>
            <Box className='componentsDescentralizado__box'>
                <Typography variant='h5' className='componentsDescentralizado__title'>
                    {languageValues.dadosContratante}
                </Typography>
                <Typography variant='h5' className='componentsDescentralizado__sub'>
                    {languageValues.preenchaDadosContratanteAssessor}
                </Typography>
            </Box>

            <Card className='componentsDescentralizado__card'>
                <Box>
                    <Row>
                        <Col cols={[12, 12, 12, 12, 12]}>
                            <Typography variant='h5' id="dadosAssessor"
                                className='componentsDescentralizado__tipo-contrato-title'>
                                {languageValues.cadastroContratoDadosAssessor}
                            </Typography>
                        </Col>
                    </Row>
                    <Row>
                        <Col cols={[12, 12, 6, 6, 6]}>
                            <NewAutoCompleteField
                                id={'empresa'}
                                label={languageValues.cadastroContratoEmpresaRequired}
                                options={empresasReducer.allList.map((item: Empresa) => ({
                                    label: `${getFormattedCNPJ(item.cnpjEmpresa)} - ${item.nome}`,
                                    value: item.id,
                                    cnpj: item.cnpjEmpresa,
                                }))}
                                value={contratosAssessoresReducer.contratoEditing.idEmpresa}
                                onChange={handleCnpjChange}
                                loading={empresasReducer.loadingAllList}
                                loadingMessage={languageValues.loadingEmpresas}
                                error={!idEmpresaIsValid().status}
                                helperText={
                                    !idEmpresaIsValid().status
                                        ? idEmpresaIsValid().error
                                        : ''}
                                disabled={
                                    empresasReducer.loadingAllList ||
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()
                                }
                            />
                        </Col>

                        <Col cols={[12, 12, 6, 6, 6]}>
                            <NewAutoCompleteField
                                id={'assessor'}
                                label={languageValues.cadastroContratoAssessorRequired}
                                placeholder={empresasReducer.loadingAllList || empresasReducer.loadingEmpresaByCnpjAssessoresNecessarios ? languageValues.loadingAssessores : ''}
                                options={(empresasReducer.empresaByCnpjAssessoresNecessarios.assessores || []).map((item: Assessor) => ({
                                    label: item.nome,
                                    value: item.cpf,
                                }))}
                                multiple
                                value={contratosAssessoresReducer.contratoEditing.cpfAssessores}
                                onChange={handleAssessorChange}
                                loading={empresasReducer.loadingAllList || empresasReducer.loadingEmpresaByCnpjAssessoresNecessarios}
                                loadingMessage={languageValues.loadingAssessores}
                                error={!cpfAssessorIsValid().status}
                                helperText={
                                    !cpfAssessorIsValid().status
                                        ? cpfAssessorIsValid().error
                                        : ''}
                                disabled={
                                    isEmpresaFieldNull() ||
                                    !idEmpresaIsValid().status ||
                                    empresasReducer.loadingAllList ||
                                    empresasReducer.loadingEmpresaByCnpjAssessoresNecessarios ||
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()
                                }
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col cols={[12, 12, 6, 6, 6]}>
                            <CurrencyNewInput
                                id={'valor-hora-assessoria'}
                                label={languageValues.contratoValorAssessoriaRequired}
                                name={'valorAssessoria'}
                                value={contratosAssessoresReducer.contratoEditing.valorAssessoria}
                                onChange={(value: number) => {
                                    handleDataChange('valorAssessoria', value);
                                }}
                                error={!valorAssessoriaIsValid().status}
                                helperText={
                                    !valorAssessoriaIsValid().status
                                        ? valorAssessoriaIsValid().error
                                        : ''}
                                disabled={
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()
                                }
                            />
                        </Col>
                        <Col cols={[12, 12, 6, 6, 6]}>
                            <CurrencyNewInput
                                id={'valor-hora-formacao'}
                                label={languageValues.contratoValorFormacaoRequired}
                                name={'valorFormacao'}
                                value={contratosAssessoresReducer.contratoEditing.valorFormacao}
                                onChange={(value: number) => {
                                    handleDataChange('valorFormacao', value);
                                }}
                                error={!valorFormacaoIsValid().status}
                                helperText={
                                    !valorFormacaoIsValid().status
                                        ? valorFormacaoIsValid().error
                                        : ''}
                                disabled={
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col cols={[12, 12, 6, 6, 6]}>
                            <CurrencyNewInput
                                id={'valor-hora-reuniao'}
                                label={languageValues.contratoValorReuniaoRequired}
                                name={'valorReuniao'}
                                value={contratosAssessoresReducer.contratoEditing.valorReuniao}
                                onChange={(value: number) => {
                                    handleDataChange('valorReuniao', value);
                                }}
                                error={!valorReuniaoIsValid().status}
                                helperText={
                                    !valorReuniaoIsValid().status
                                        ? valorReuniaoIsValid().error
                                        : ''
                                }
                                disabled={
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()
                                }
                            />

                        </Col>
                        <Col cols={[12, 12, 6, 6, 6]}>
                            <CurrencyNewInput
                                id={'valor-hora-outros'}
                                label={languageValues.contratoAssessorPedagogicoOutrosType}
                                name={'valorOutros'}
                                value={contratosAssessoresReducer.contratoEditing.valorOutros}
                                onChange={(value: number) => {
                                    handleDataChange('valorOutros', value);
                                }}
                                helperText={''}
                                error={false}
                                disabled={
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col cols={[12, 12, 12, 12, 12]}>
                            <Typography variant='h5' id="dadosAssessor" className='componentsDescentralizado__tipo-contrato-title'>
                                {languageValues.cadastroContratoDadosContrato}
                            </Typography>
                        </Col>
                    </Row>

                    <Row>
                        <Col cols={[12, 12, 12, 12, 12]}>
                            <CustomMultipleSelect
                                id={'programas-educacionais'}
                                label={languageValues.programasEducacionais}
                                options={allProgramas() && allProgramas().map((item) => ({
                                    label: item.description,
                                    value: item.value,
                                }))}
                                multiple
                                value={contratosAssessoresReducer.contratoEditing.programasEducacionais}
                                onChange={(value) => {
                                    handleDataChange('programasEducacionais', value);
                                }}
                                error={!valueProgramaEducacionaisIsValid().status}
                                helperText={
                                    !valueProgramaEducacionaisIsValid().status
                                        ? valueProgramaEducacionaisIsValid().error
                                        : ''}
                                disabled={
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()}
                                menuMaxHeight={{
                                    desktop: AlturaMenuEnum.full,
                                    tablet: AlturaMenuEnum.full,
                                    mobile: AlturaMenuEnum.full
                                }}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col cols={[12, 12, 6, 6, 6]}>
                            <CustomTextField
                                id={'data-inicio'}
                                label={languageValues.cadastroContratoDataInicio}
                                name='dataInicio'
                                value={contratosAssessoresReducer.contratoEditing.dataInicio}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const value = event.target.value.trim();
                                    handleDataChange('dataInicio', value);
                                    if (value.length === 10) {
                                        onDataInicioChanged(value);
                                    } else {
                                        setDataInicioError('');
                                    }
                                }}
                                InputProps={{
                                    inputComponent: DateMaskedInput,
                                }}
                                helperText={
                                    dataInicioError ||
                                    (!dataInicioValidacaoResultado.status ? dataInicioValidacaoResultado.error : '')
                                }
                                error={!dataInicioValidacaoResultado.status || !!dataInicioError}
                                disabled={
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()}
                            />
                        </Col>
                        <Col cols={[12, 12, 6, 6, 6]}>
                            <CustomTextField
                                id={'data-fim'}
                                label={languageValues.cadastroContratoDataFim}
                                name='dataFim'
                                value={contratosAssessoresReducer.contratoEditing.dataFim}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const value = event.target.value.trim();
                                    handleDataChange('dataFim', value);
                                    if (value.length === 10) {
                                        onDataFimChanged(value);
                                    } else {
                                        setDataFimError('');
                                    }
                                }}
                                InputProps={{
                                    inputComponent: DateMaskedInput,
                                }}
                                helperText={
                                    dataFimError ||
                                    (!dataFimValidacaoResultado.status ? dataFimValidacaoResultado.error : '')
                                }
                                error={!dataFimValidacaoResultado.status || !!dataFimError}
                                disabled={
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()} />
                        </Col>
                    </Row>

                    <Row className="componentsDescentralizado__row-content">
                        <Col cols={[12, 12, 6, 6, 6]}>
                            <div className='componentsDescentralizado__label-total-meses'>
                                {languageValues.totalMesesContratados}
                            </div>
                            <div className='componentsDescentralizado__value-total-meses'>
                                {isNaN(contratosAssessoresReducer.contratoEditing.totalMeses)
                                    ? 0
                                    : Number(contratosAssessoresReducer.contratoEditing.totalMeses).toString()}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col cols={[12, 12, 6, 6, 6]}>
                            <CustomCheckbox
                                id={'analise-juridica'}
                                className={'componentsDescentralizado__analise-juridica'}
                                label={languageValues.flgAnaliseJuridicaAceite}
                                name='flgAnaliseJuridica'
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    handleDataChange('flgAnaliseJuridica', event.target.checked);
                                }}
                                checked={contratosAssessoresReducer.contratoEditing.flgAnaliseJuridica}
                                disabled={
                                    !isContratoAtivoAssessor() ||
                                    !validaStatusContratoFluxo()}
                            />
                        </Col>
                    </Row>
                </Box>
            </Card>
        </Container>
    );
};

